import React, { useState } from 'react'
import {
	EuiButton,
	EuiButtonEmpty,
	EuiFormRow,
	EuiModal,
	EuiModalBody,
	EuiModalFooter,
	EuiModalHeader,
	EuiModalHeaderTitle,
	EuiTextArea,
} from '@equipmentshare/ds2'
import { heapEvent } from '@equipmentshare/heaper'
import { useApproveTransferOrderMutation, useRejectTransferOrderMutation } from '../../../graphql'
import { useShowError, useShowSuccess } from 'hooks/useToast'

type Props = {
	transferOrderId: string,
	action: 'approve' | 'reject',
	onClose: () => void,
	onSuccess: () => void,
}

export default function ApproveRejectTransferOrderModal( { transferOrderId, action, onClose, onSuccess }: Props ) {
	const [ approverNote, setApproverNote ] = useState( '' )
	const showSuccess = useShowSuccess()
	const showError = useShowError()
	const [ approveTransferOrder, { loading: approveLoading } ] = useApproveTransferOrderMutation()
	const [ rejectTransferOrder, { loading: rejectLoading } ] = useRejectTransferOrderMutation()
	const loading = approveLoading || rejectLoading

	const isApprove = action === 'approve'
	const title = isApprove ? 'Approve Transfer Order' : 'Reject Transfer Order'
	const noteLabel = isApprove ? 'Approver Note (optional)' : 'Rejection Note (optional)'
	const notePlaceholder = isApprove ? 'Note about this approval...' : 'Reason for rejection...'
	const confirmLabel = isApprove ? 'Approve' : 'Reject'
	const confirmColor = isApprove ? 'success' : 'danger'

	const handleConfirm = async() => {
		const input = {
			transferOrderId,
			approverNote: approverNote.trim() || undefined,
		}

		if ( isApprove ) {
			const { data } = await approveTransferOrder( {
				variables: { input },
			} )

			if ( data?.approveTransferOrder?.success ) {
				heapEvent( 'Inventory - Transfer Orders - Approve - Submit - Success', { transfer_order_id: transferOrderId } )
				showSuccess( `approveTransferOrderSuccess${Date.now()}`, data.approveTransferOrder.message )
				onSuccess()
			} else {
				heapEvent( 'Inventory - Transfer Orders - Approve - Submit - Error', { transfer_order_id: transferOrderId } )
				showError( `approveTransferOrderError${Date.now()}`, data?.approveTransferOrder?.message ?? 'Failed to approve transfer order.' )
			}
		} else {
			const { data } = await rejectTransferOrder( {
				variables: { input },
			} )

			if ( data?.rejectTransferOrder?.success ) {
				heapEvent( 'Inventory - Transfer Orders - Reject - Submit - Success', { transfer_order_id: transferOrderId } )
				showSuccess( `rejectTransferOrderSuccess${Date.now()}`, data.rejectTransferOrder.message )
				onSuccess()
			} else {
				heapEvent( 'Inventory - Transfer Orders - Reject - Submit - Error', { transfer_order_id: transferOrderId } )
				showError( `rejectTransferOrderError${Date.now()}`, data?.rejectTransferOrder?.message ?? 'Failed to reject transfer order.' )
			}
		}
	}

	return (
		<EuiModal onClose={onClose}>
			<EuiModalHeader>
				<EuiModalHeaderTitle>{title}</EuiModalHeaderTitle>
			</EuiModalHeader>
			<EuiModalBody>
				<EuiFormRow label={noteLabel}>
					<EuiTextArea
						value={approverNote}
						onChange={( e ) => setApproverNote( e.target.value )}
						placeholder={notePlaceholder}
						rows={3}
					/>
				</EuiFormRow>
			</EuiModalBody>
			<EuiModalFooter>
				<EuiButtonEmpty onClick={onClose}>
					Back
				</EuiButtonEmpty>
				<EuiButton color={confirmColor} fill onClick={handleConfirm} isLoading={loading}>
					{confirmLabel}
				</EuiButton>
			</EuiModalFooter>
		</EuiModal>
	)
}
